import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function KingstonUponThamesCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Kingston Pothole Claims | 4,793 Repairs in One Year | Fixtyer"
        description="Kingston upon Thames filled 4,793 potholes in 2024 alone and good-condition roads fell from 96.6% to 71.9% in four years. The DfT rates condition RED on incomplete data. See what that means for your claim."
        canonical="/councils/kingston-upon-thames"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Kingston upon Thames', url: '/councils/kingston-upon-thames' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Kingston spends well over triple its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Kingston projects £1.75m of council-allocated capital spend against a £543,000 DfT allocation in 2025/26. But the Condition scorecard is RED — and the DfT notes Kingston\'s ratings are based on incomplete road condition data. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 271.32km — 79.4% of Kingston\'s 341.52km carriageway network. The council uses RoadAI by Vaisala to support officer visual assessments on unclassified roads rather than SCANNER laser surveys. Condition data is published as borough-wide segment percentages, not broken down by road class — so establishing what the council knew about your specific residential street requires prior reports, photos and repair records.',
          },
          {
            question: 'Why does the DfT rate Kingston RED on condition when the council publishes survey data?',
            answer: 'The DfT flags Kingston\'s overall, condition and best-practice scorecards as based on incomplete road condition data. The council\'s transparency report publishes aggregate condition percentages across surveyed road segments — green roads fell from 96.61% in 2021 to 71.89% in 2024 — but does not split results by A, B/C and U-road class in the same table. Best Practice is also RED, consistent with gaps in the asset-management evidence the DfT expects.',
          },
          {
            question: 'Kingston filled 4,793 potholes in 2024 — does that mean the roads are fixed?',
            answer: 'No. The 4,793 figure is nearly four times the 1,293 potholes filled in 2023 and more than double any other year in the published table. Reactive maintenance accounted for 41.4% of total maintenance spend in 2024/25, up from 23.8% in 2020/21. A spike in pothole repairs is evidence of defect formation, not elimination — especially where prior reports or photos show your specific pothole predated any repair.',
          },
          {
            question: 'Does Kingston\'s RoadAI survey coverage help the council under Section 58?',
            answer: 'It helps only if the council can show your road was surveyed, the defect was visible, and it was repaired within reasonable timescales. Kingston states RoadAI now covers over 85% of the borough\'s public highway boundary, but the council also notes it started using the tool in 2020 when pandemic restrictions disrupted work — and a new PAS 2161 methodology is replacing the three-category green/amber/red system. Your claim should focus on the specific defect and any prior reports, not borough-wide survey coverage alone.',
          },
          {
            question: 'Does Kingston\'s Section 58 streetworks admission relate to my pothole claim?',
            answer: 'It is a different statute, but it shows how the council thinks about liability. Kingston states that using the nationally recognised DfT Streetworks system, it can identify issues such as Section 58s while coordinating work among statutory undertakers and highways teams. Your pothole claim turns on Section 58 of the Highways Act 1980 — whether the council took reasonable care to maintain the specific carriageway — but the council\'s own transparency report explicitly links reactive maintenance to Section 41 and Section 58 statutory undertakings.',
          },
          {
            question: 'Should I report the pothole to Kingston before claiming?',
            answer: 'Reporting creates a dated council record that can prove actual notice if the defect was reported before your incident. Kingston\'s transparency report covers reactive maintenance undertaken in line with Section 41 and Section 58 statutory duties. A prior report through the council\'s online pothole service or FixMyStreet — combined with photos showing the defect\'s age — is often stronger evidence than network-level condition percentages alone.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Kingston upon Thames Council"
        councilSlug="kingston-upon-thames"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/kingston-upon-thames"
        headline="Kingston upon Thames Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Kingston upon Thames</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Kingston: Good-Condition Roads Fell from 97% to 72% in Four Years
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            The Royal Borough earns <strong className="text-emerald-400">GREEN</strong> for spend — projecting
            {' '}<strong className="text-emerald-400">£1.75m</strong> in council capital works against a
            {' '}<strong className="text-emerald-400">£543,000</strong> DfT allocation. Yet the DfT awards
            {' '}<strong className="text-rose-400">RED</strong> for condition and best practice, flags
            {' '}<strong className="text-rose-400">incomplete road condition data</strong>, and the council&apos;s
            own survey table shows green-condition roads collapsing from
            {' '}<strong className="text-rose-400">96.61% in 2021 to 71.89% in 2024</strong> — while pothole
            fills jumped to <strong className="text-rose-400">4,793 in a single year</strong>.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">4,793</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2024 alone</div>
            <div className="text-brand-300 text-sm mt-1">
              Nearly four times the 1,293 filled in 2023 — and more than double any other year in Kingston&apos;s
              published transparency table, on a 341.52km network where 271.32km is unclassified.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard caveat */}
      <section className="py-12 bg-amber-50 border-b border-amber-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <div className="text-3xl">⚠️</div>
            <div>
              <h2 className="font-semibold text-amber-900 mb-2">DfT data caveat</h2>
              <p className="text-amber-800 text-sm">
                The Department for Transport notes that Kingston upon Thames&apos;s overall, condition and
                best-practice scorecards are <strong>based on incomplete road condition data</strong>, which
                has affected these ratings. The council publishes borough-wide segment percentages but not
                a class-by-class A, B/C and U-road breakdown in its transparency report. Treat headline
                scorecards as indicative — your claim turns on the specific defect, prior reports and repair records.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of surveyed road-segment data from Kingston&apos;s own transparency report — a steady
            slide from near-universal green to nearly one in four roads amber
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Roads surveyed</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">Green</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-amber-700">Amber</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">Red</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center">566</td>
                  <td className="px-6 py-4 text-center">95.94%</td>
                  <td className="px-6 py-4 text-center">3.89%</td>
                  <td className="px-6 py-4 text-center">0.18%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-emerald-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center">766</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">96.61%</td>
                  <td className="px-6 py-4 text-center">3.26%</td>
                  <td className="px-6 py-4 text-center">0.13%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center">774</td>
                  <td className="px-6 py-4 text-center">84.63%</td>
                  <td className="px-6 py-4 text-center">13.82%</td>
                  <td className="px-6 py-4 text-center">1.55%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center">937</td>
                  <td className="px-6 py-4 text-center">84.10%</td>
                  <td className="px-6 py-4 text-center">13.87%</td>
                  <td className="px-6 py-4 text-center">2.03%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold">925</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">71.89%</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">24.22%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3.89%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="text-center bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="text-3xl font-bold text-emerald-600">−24.7pp</div>
              <div className="text-sm text-emerald-800">Green share lost since 2021</div>
            </div>
            <div className="text-center bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="text-3xl font-bold text-amber-600">24.22%</div>
              <div className="text-sm text-amber-800">Amber roads in 2024 — up from 3.26%</div>
            </div>
            <div className="text-center bg-rose-50 border border-rose-200 rounded-xl p-4">
              <div className="text-3xl font-bold text-rose-600">30×</div>
              <div className="text-sm text-rose-800">RED share rise — 0.13% to 3.89% since 2021</div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-3">How Kingston defines the categories</h3>
            <ul className="text-amber-800 text-sm space-y-1">
              <li>• <strong>Green:</strong> no further investigation or treatment required (above 67%)</li>
              <li>• <strong>Amber:</strong> maintenance may be required soon (between 34% and 67%)</li>
              <li>• <strong>Red:</strong> should be considered for maintenance (below 33%)</li>
            </ul>
            <p className="text-amber-800 text-sm mt-3">
              By 2024, nearly a quarter of surveyed segments sit in amber — maintenance may be required soon —
              and 3.89% are formally in red. That is a very different network picture from 2021, when 96.61%
              of segments needed no further treatment.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Survey coverage is rising — condition is not</h3>
            <p className="text-brand-200 mb-4">
              Kingston states it started using RoadAI in 2020, when pandemic restrictions disrupted work, and
              survey coverage has since grown to over <strong className="text-white">85% of the borough&apos;s
              public highway boundary</strong>. Roads surveyed rose from 566 segments in 2020 to 925 in 2024.
              Yet green-condition share fell sharply over the same period — more measurement, worse results.
            </p>
            <p className="text-white font-medium">
              The DfT&apos;s RED condition rating and incomplete-data flag sit against this backdrop: a council
              that can see more of its network but publishes a deteriorating condition trend.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Borough */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 271km Residential Network
          </h2>
          <p className="text-surface-600 mb-8">
            Nearly four-fifths of Kingston&apos;s carriageways are unclassified — where most pothole damage happens
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road type</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center">41.09</td>
                  <td className="px-6 py-4 text-center">12.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center">29.11</td>
                  <td className="px-6 py-4 text-center">8.5%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-bold text-brand-900">U roads</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">271.32</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">79.4%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total roads</td>
                  <td className="px-6 py-4 text-center font-bold">341.52</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">SCANNER on classified, RoadAI on local</h3>
              <p className="text-surface-600 text-sm mb-3">
                Kingston states that condition assessments on the local classified road network use SCANNER
                laser-based technology, while unclassified roads are assessed with RoadAI by Vaisala — a
                third-party tool that supports officer visual assessments and helps compile annual maintenance
                programmes.
              </p>
              <p className="text-surface-600 text-sm">
                The published condition table does not split green, amber and red percentages by road class.
                If your pothole was on a residential street, you cannot read your road&apos;s RAG status directly
                from the transparency report — only infer borough-wide deterioration.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Resurfacing vs network size</h3>
              <p className="text-amber-800 text-sm mb-3">
                In the most recent financial year, Kingston&apos;s planned maintenance section resurfaced
                approximately <strong>6.25km of roads</strong> and reconstructed around <strong>2km of footways</strong> —
                on a 341.52km carriageway network with 373.6km of footways.
              </p>
              <p className="text-amber-800 text-sm">
                That is roughly 1.8% of the carriageway network treated in a single year. The remaining
                98%+ relies on reactive patching, RoadAI surveys and routine safety inspections to catch
                defects before they damage vehicles.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Kingston must show it had a reasonable system for knowing
              the condition of the road that damaged your vehicle. On a borough that is 79% U-roads, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road in the 85%+ of the network RoadAI covers — or the remainder without a published score?</li>
              <li>• If borough-wide green share fell from 96.61% to 71.89%, what was done about your street?</li>
              <li>• Were there prior reports (FixMyStreet, council online reports) before your incident?</li>
              <li>• Does a patch on a 271km U-road network amount to reasonable maintenance — or reactive firefighting?</li>
            </ul>
            <p className="text-white font-medium">
              Aggregate condition percentages on a residential-heavy borough say little about your specific
              defect without dated reports and photographs.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            10,738 Potholes in Five Years — 4,793 in One
          </h2>
          <p className="text-surface-600 mb-8">
            Kingston&apos;s own estimated pothole-fill counts — and the 2024 spike that stands out
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center">1,898</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center">1,627</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center">1,127</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center">1,293</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,793</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2025</td>
                  <td className="px-6 py-4 text-center">1,861</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2020–2024 total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10,738</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~13 potholes a day in 2024</h3>
              <p className="text-surface-600 text-sm">
                The 4,793 potholes filled in 2024 average roughly 13 repairs every day across Kingston&apos;s
                341.52km network. A network producing defects at that rate is one where potholes routinely
                form between inspections — exactly the scenario where prior reports and photographic evidence
                decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Reactive spend is rising</h3>
              <p className="text-amber-800 text-sm">
                Kingston classifies reactive maintenance — including immediate repairs in line with Section 41
                and Section 58 statutory undertakings — at <strong>41.4% of total spend in 2024/25</strong>,
                up from 23.8% in 2020/21. Preventative share fell from 76.2% to 58.6% over the same period.
                More patching, less prevention — on a network whose surveyed condition is deteriorating.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spend */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Spend, Shifting Priorities
          </h2>
          <p className="text-surface-600 mb-8">
            Kingston invests well beyond its DfT capital allocation — but reactive maintenance is taking a larger share
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Financial year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Council capital</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100 bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£543,000</td>
                  <td className="px-4 py-3 text-center font-bold">£1,750,000</td>
                  <td className="px-4 py-3 text-center">£1,173,600</td>
                  <td className="px-4 py-3 text-center text-emerald-600 font-bold">66.1%</td>
                  <td className="px-4 py-3 text-center">33.9%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£167,000</td>
                  <td className="px-4 py-3 text-center">£1,750,000</td>
                  <td className="px-4 py-3 text-center">£1,356,601</td>
                  <td className="px-4 py-3 text-center">58.6%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">41.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£167,000</td>
                  <td className="px-4 py-3 text-center">£1,750,000</td>
                  <td className="px-4 py-3 text-center">£1,152,553</td>
                  <td className="px-4 py-3 text-center">62.5%</td>
                  <td className="px-4 py-3 text-center">37.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£1,750,000</td>
                  <td className="px-4 py-3 text-center">£1,179,481</td>
                  <td className="px-4 py-3 text-center">59.7%</td>
                  <td className="px-4 py-3 text-center">40.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£1,500,000</td>
                  <td className="px-4 py-3 text-center">£961,206</td>
                  <td className="px-4 py-3 text-center">60.9%</td>
                  <td className="px-4 py-3 text-center">39.1%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£2,000,000</td>
                  <td className="px-4 py-3 text-center">£626,222</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">76.2%</td>
                  <td className="px-4 py-3 text-center">23.8%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Well funded — but patching more</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£543k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£1.75m</div>
                <div className="text-brand-300 text-sm">Council capital spend (projected)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">41.4%</div>
                <div className="text-brand-300 text-sm">Reactive share in 2024/25 — highest in five years</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Kingston spends over <span className="text-emerald-400">three times its DfT allocation</span> from
              council capital alone — yet reactive maintenance has nearly doubled as a share of total spend since
              2020/21, and pothole fills hit a record 4,793 in 2024. The problem is not only the chequebook.
            </p>
          </div>
        </div>
      </section>

      {/* Elastomac & methodology */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Elastomac, PAS 2161 and the RED Best Practice Rating
          </h2>
          <p className="text-surface-600 mb-8">
            What Kingston says about reactive innovation — and the methodology change ahead
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="With the large number of unclassified roads we have within our public highway boundary, we use a package called RoadAI by a company called Vaisala. This is used to support officer visual assessments of the condition of a road. It provides a third party independent score which we use to help compile our annual maintenance programmes."
              source="Royal Borough of Kingston upon Thames Local Highways Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="From this year a new methodology will be used based on the BSI PAS2161 standard. Local highway authorities will be required to use a supplier that has been accredited against PAS2161. This new standard will place roads into 5 categories instead of 3 to help government gain a more detailed understanding of road conditions in England."
              source="Royal Borough of Kingston upon Thames Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Elastomac rollout</h3>
              <p className="text-emerald-800 text-sm mb-3">
                Kingston is using Elastomac across the borough following a successful trial in 2024 — a surface
                dressing effective for joints, alligator cracking and general wear. The council states roads can
                reopen 30 minutes after application, reducing traffic disruption.
              </p>
              <p className="text-emerald-800 text-sm">
                As of June 2025, the council had resurfaced <strong>5,285 square metres</strong> using Elastomac.
                That is a targeted reactive tool — not a borough-wide resurfacing programme on 341.52km of roads.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Why Best Practice is RED</h3>
              <p className="text-rose-800 text-sm mb-3">
                The DfT Best Practice scorecard is RED alongside the incomplete condition-data flag. Kingston
                publishes aggregate segment condition percentages but not the class-by-class breakdown the DfT
                framework expects, and is transitioning from three RAG categories to PAS 2161&apos;s five-tier system.
              </p>
              <p className="text-rose-800 text-sm">
                That weakens the asset-management evidence Kingston can produce for a blanket Section 58 defence
                — even where individual repair processes are documented.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Using the nationally recognised DfT street works system, we identify issues such Section 58s while coordinating work activities among statutory undertakers and highways teams."
            source="Royal Borough of Kingston upon Thames Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Claims analysis */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Spend, RED-Condition Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Kingston invests — but its own data shows deterioration and incomplete reporting
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — council capital spend roughly triples the DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> RoadAI surveys now cover over 85% of the borough&apos;s highway boundary
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Elastomac and planned maintenance programme with published 2025/26 schedule
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented streetworks coordination and Section 58 notice processes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 66.1% preventative share projected for 2025/26
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a spend-backed Section 58 defence on main roads. Generic claims will struggle without defect-specific evidence.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What works in yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED condition scorecard — DfT flags incomplete road condition data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Green-condition roads fell from 96.61% to 71.89% in four years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 79.4% of network is U-roads without published class-level RAG breakdown
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 4,793 pothole fills in 2024 — record reactive volume on a deteriorating network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive maintenance share rose from 23.8% to 41.4% since 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED best practice — gaps in the condition evidence the DfT expects
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and expanding RoadAI coverage, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, Kingston&apos;s online reporting service) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, cite the 271.32km network and missing class-level condition breakdown</li>
              <li>• Whether the 2024 pothole spike and condition decline predate your incident</li>
              <li>• Whether a patch or Elastomac treatment failed — relevant to recurring defects</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Kingston&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Kingston first
          </h2>
          <p className="text-surface-600 mb-6">
            Kingston&apos;s transparency report covers reactive maintenance undertaken in line with Section 41
            and Section 58 statutory undertakings. Reporting the defect through the council creates a dated
            record — useful evidence if the pothole was reported before your incident, or if the council failed
            to repair it within a reasonable time.
          </p>
          <a
            href="https://www.kingston.gov.uk/roads-and-travel/report-problem/potholes-pavements"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Kingston Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use the council&apos;s interactive map or online form. Keep your reference number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Kingston upon Thames?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded borough with RED condition data demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No incomplete-data caveat cited</li>
                <li>• No 4,793 repair spike referenced</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ Condition decline from 96.61% to 71.89% documented</li>
                <li>✅ 271km U-road network and survey-gap argued</li>
                <li>✅ 4,793 pothole fills in 2024 cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Kingston</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=kingston-upon-thames"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Kingston Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Kingston spends well over triple its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Kingston projects £1.75m of council-allocated
                capital spend against a £543,000 DfT allocation in 2025/26. But the Condition scorecard is
                RED — and the DfT notes Kingston&apos;s ratings are based on incomplete road condition data.
                Section 58 turns on whether the specific defect that damaged your vehicle was reasonably
                inspected and repaired, not on aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 271.32km — 79.4% of Kingston&apos;s 341.52km carriageway network. The council
                uses RoadAI by Vaisala to support officer visual assessments on unclassified roads rather than
                SCANNER laser surveys. Condition data is published as borough-wide segment percentages, not
                broken down by road class — so establishing what the council knew about your specific residential
                street requires prior reports, photos and repair records.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why does the DfT rate Kingston RED on condition when the council publishes survey data?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT flags Kingston&apos;s overall, condition and best-practice scorecards as based on
                incomplete road condition data. The council&apos;s transparency report publishes aggregate
                condition percentages across surveyed road segments — green roads fell from 96.61% in 2021 to
                71.89% in 2024 — but does not split results by A, B/C and U-road class in the same table.
                Best Practice is also RED, consistent with gaps in the asset-management evidence the DfT expects.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Kingston filled 4,793 potholes in 2024 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The 4,793 figure is nearly four times the 1,293 potholes filled in 2023 and more than double
                any other year in the published table. Reactive maintenance accounted for 41.4% of total maintenance
                spend in 2024/25, up from 23.8% in 2020/21. A spike in pothole repairs is evidence of defect
                formation, not elimination — especially where prior reports or photos show your specific pothole
                predated any repair.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Kingston&apos;s RoadAI survey coverage help the council under Section 58?
              </h3>
              <p className="text-surface-600 text-sm">
                It helps only if the council can show your road was surveyed, the defect was visible, and it was
                repaired within reasonable timescales. Kingston states RoadAI now covers over 85% of the borough&apos;s
                public highway boundary, but the council also notes it started using the tool in 2020 when pandemic
                restrictions disrupted work — and a new PAS 2161 methodology is replacing the three-category
                green/amber/red system. Your claim should focus on the specific defect and any prior reports, not
                borough-wide survey coverage alone.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Should I report the pothole to Kingston before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Reporting creates a dated council record that can prove actual notice if the defect was reported
                before your incident. Kingston&apos;s transparency report covers reactive maintenance undertaken in
                line with Section 41 and Section 58 statutory duties. A prior report through the council&apos;s
                online pothole service or FixMyStreet — combined with photos showing the defect&apos;s age — is
                often stronger evidence than network-level condition percentages alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.kingston.gov.uk/roads-and-travel/roadworks-maintenance/resurfacing/transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Royal Borough of Kingston upon Thames Local Highways Maintenance Transparency Report (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
